/**
 * SalesTeams Component
 *
 * This component manages the sales team members in the CMS system. It provides functionality to:
 * - View all sales team members in a table format
 * - Add new sales team members
 * - Edit existing sales team members
 * - Toggle between active/inactive status
 * - View and manage reporting relationships
 */

import React, { useEffect, useState } from 'react';
import Modal from '../../../components/ModalComponents/Modal';
import { createSalesTeam, updateSalesTeam } from '../../../api/sales';
import { getUsersList } from '../../../api/administration';
import { getUserInfoDetails } from '../../../utlis';
import { useSelector } from 'react-redux';
import axiosInstance from '../../../api/axiosInstance';
import { toast } from 'react-toastify';
import CreateEditForm from './components/CreateEditForm';
import SalesTable from './components/SalesTable';
import SalesTeamHeader from './components/SalesTeamHeader';
import { useForm } from 'react-hook-form';
import { formatPhoneNumber } from './components/utils';

// Get user context information for API calls
const { companyId, clientId } = getUserInfoDetails();

// Initial state for a new sales team member - moved outside component to prevent recreation
const initialSalesTeam = {
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  jobTitle: '',
  reportingManager: '',
  loginId: '',
  mobileNum: '',
  email: '',
  contactName: '',
  contactPhoneNum: '',
  contactEmail: '',
  contactRelation: '',
  notes: '',
  salesStatus: 'active',
};

const SalesTeams = () => {
  // Form handling with react-hook-form
  const { user_id } = getUserInfoDetails();
  const { reset } = useForm({
    defaultValues: {
      salesStatus: 'active',
      user_id: user_id,
    },
  });

  // Redux selectors for reference data
  const jobs = useSelector((state) => state.reference.salesteamjobtitles);
  const relations = useSelector((state) => state.reference.relations);

  // State management
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [salesTeam, setSalesTeam] = useState(initialSalesTeam);
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // State for managers data
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // State for users (Login ID dropdown)
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Separate state for form submission (doesn't affect the listing)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for API field errors to display on the form
  const [apiFieldErrors, setApiFieldErrors] = useState({});

  /**
   * Effect hook to reset form data when salesTeam changes
   * Only runs when a real salesTeam object is set (not the initial empty state)
   */
  // Only reset form when editing, not when adding a new person
  useEffect(() => {
    // Only reset if editing (salesTeam._id exists)
    if (salesTeam._id) {
      const formData = {
        firstName: salesTeam.firstName || '',
        middleName: salesTeam.middleName || '',
        lastName: salesTeam.lastName || '',
        preferredName: salesTeam.preferredName || '',
        jobTitle: salesTeam.jobTitle || '',
        reportingManager: salesTeam.reportingManager || '',
        loginId: salesTeam.loginId || '',
        mobileNum: salesTeam.mobileNum || '',
        email: salesTeam.email || '',
        contactName: salesTeam.contactName || '',
        contactPhoneNum: salesTeam.contactPhoneNum || '',
        contactEmail: salesTeam.contactEmail || '',
        contactRelation: salesTeam.contactRelation || '',
        notes: salesTeam.notes || '',
        salesStatus: salesTeam.salesStatus || 'active',
      };
      reset(formData);
    }
    // Do not reset when adding a new person (no _id)
  }, [salesTeam, reset]);

  /**
   * Modal control functions
   * Manages the visibility of the create/edit form modal
   */
  const openModal = () => {
    setSalesTeam(initialSalesTeam);
    reset(initialSalesTeam);
    setApiFieldErrors({}); // Clear any previous API errors
    setIsModalOpen(true);
    fetchModalData(); // Load managers and users when modal opens
    document.body.style.overflow = 'hidden';
  };

  /**
   * Closes the modal and resets the form
   */
  const handleClose = () => {
    setIsModalOpen(false);
    setSalesTeam(initialSalesTeam);
    reset(initialSalesTeam);
    setApiFieldErrors({}); // Clear API errors on close
    document.body.style.overflow = '';
  };

  // Fetch sales team list only
  const getData = async (showToast = true) => {
    setLoading(true);

    try {
      const params = {
        company: companyId,
        client: clientId,
        status: 'active',
      };

      const salesData = await axiosInstance.get('/salesteam', { params });
      setData(salesData.data?.data?.records || []);

      if (showToast) {
        toast.success('Data refreshed successfully', { toastId: 'refresh-success' });
      }
    } catch (error) {
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message || error.message || '';

      if (error.message === 'Network Error' || !error.response) {
        toast.error('Unable to connect to server. Please check your connection');
      } else if (statusCode === 401) {
        toast.error('Session expired. Please log in again');
      } else if (statusCode === 403) {
        toast.error('Access denied: You do not have permission to view sales team');
      } else if (statusCode === 500) {
        toast.error('Server error. Please try again later');
      } else {
        toast.error(errorMessage || 'Failed to load sales team');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch managers and users on demand (when modal opens)
  const fetchModalData = async () => {
    setManagersLoading(true);
    setUsersLoading(true);
    try {
      const params = {
        company: companyId,
        client: clientId,
        status: 'active',
      };

      const [managersData, usersResponse] = await Promise.all([
        axiosInstance.get('/salesteam', {
          params: { ...params, userType: 'manager' },
        }),
        getUsersList(1, 100),
      ]);

      setManagers(managersData.data?.data?.records || []);

      if (usersResponse && usersResponse.data) {
        const usersData = usersResponse.data;
        const usersList = usersData.data?.records || usersData.records || usersData.users || [];
        const activeUsers = usersList.filter((user) => {
          const status = user.status || '';
          const isActive = user.is_active;
          return (
            status.toUpperCase() === 'ACTIVE' ||
            status === 'Active' ||
            isActive === true ||
            isActive === 1 ||
            isActive === '1'
          );
        });
        setUsers(activeUsers);
      }
    } catch (err) {
      // Silently handle — modal will show empty dropdowns
    } finally {
      setManagersLoading(false);
      setUsersLoading(false);
    }
  };

  /**
   * Handles form submission for creating or updating a sales team member
   * @param {Object} formData - The form data to be submitted
   */
  const onSubmit = async (formData) => {
    // Validate required fields
    // Reporting Manager is optional when Job Title is Manager
    const isManager = formData.jobTitle?.toLowerCase() === 'manager';
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'mobileNum',
      'jobTitle',
      ...(isManager ? [] : ['reportingManager']),
      'contactName',
      'contactPhoneNum',
      'contactEmail',
      'contactRelation',
    ];
    const missingFields = requiredFields.filter((field) => {
      const value = formData[field];
      if (value === null || value === undefined) return true;
      if (typeof value === 'string') return value.trim() === '';
      if (typeof value === 'number') return false; // Numbers are valid
      if (typeof value === 'boolean') return false; // Booleans are valid
      return !value; // For other types, check if truthy
    });

    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    const { reportingManager, loginId, mobileNum, contactPhoneNum, salesStatus, contactRelation, ...rest } =
      formData;

    // Strip formatting from phone numbers (send raw digits to API)
    const mobileDigits = (mobileNum || '').replace(/\D/g, '');
    const contactPhoneDigits = (contactPhoneNum || '').replace(/\D/g, '');

    // Convert loginId to userId - ensure proper number conversion
    // Only set userId if loginId has a valid value
    const hasUserId = loginId && loginId !== '' && !isNaN(Number(loginId));
    const userIdValue = hasUserId ? Number(loginId) : undefined;

    let payload;
    if (salesTeam._id) {
      // Update: send salesPersonId from API data, not managerId from form
      // Find the correct sales person record from data
      const personRecord = data.find((d) => d.sales_person_id === salesTeam._id);
      // Remove unwanted fields from rest (remove _id only for update)
      const { _id, ...restUpdate } = rest;
      payload = {
        ...restUpdate,
        salesPersonId: personRecord ? Number(personRecord.sales_person_id) : undefined,
        managerId: reportingManager ? Number(reportingManager) : undefined,
        mobile: mobileDigits,
        contactMobile: contactPhoneDigits,
        relation: contactRelation,
        status: salesStatus,
      };
      // Only include userId if it has a valid value
      if (userIdValue !== undefined) {
        payload.userId = userIdValue;
      }
    } else {
      // Add: send as managerId from form
      payload = {
        ...rest,
        managerId: reportingManager ? Number(reportingManager) : undefined,
        mobile: mobileDigits,
        contactMobile: contactPhoneDigits,
        relation: contactRelation,
        status: salesStatus,
        company: companyId,
        client: clientId,
      };
      // Only include userId if it has a valid value
      if (userIdValue !== undefined) {
        payload.userId = userIdValue;
      }
    }

    try {
      // Use isSubmitting instead of isLoading to avoid affecting the background listing
      setIsSubmitting(true);

      if (salesTeam._id) {
        // Update existing sales team member
        await updateSalesTeam(salesTeam._id, payload);
        toast.success('Sales Team Updated Successfully');
        // Close modal first, then refresh data
        handleClose();
        // Refresh listing after modal is closed
        await getData(false);
      } else {
        // Create new sales team member
        await createSalesTeam(payload);
        toast.success('Sales Team Created Successfully');
        // Close modal first, then refresh data
        handleClose();
        // Refresh listing after modal is closed
        await getData(false);
      }
    } catch (error) {

      // Map API field names to form field names
      const apiToFormFieldMap = {
        mobile: 'mobileNum',
        email: 'email',
        contactMobile: 'contactPhoneNum',
        contactEmail: 'contactEmail',
        firstName: 'firstName',
        lastName: 'lastName',
        contact_mobile: 'contactPhoneNum',
        contact_email: 'contactEmail',
        first_name: 'firstName',
        last_name: 'lastName',
      };

      // Map API field names to user-friendly display names
      const fieldDisplayNames = {
        mobile: 'Mobile Number',
        mobileNum: 'Mobile Number',
        email: 'Email',
        contactMobile: 'Contact Phone Number',
        contactPhoneNum: 'Contact Phone Number',
        contactEmail: 'Contact Email',
        firstName: 'First Name',
        lastName: 'Last Name',
        contact_mobile: 'Contact Phone Number',
        contact_email: 'Contact Email',
        first_name: 'First Name',
        last_name: 'Last Name',
      };

      // Handle API errors with appropriate messages
      if (error.response) {
        const { status, data: responseData } = error.response;
        const apiMessage = responseData?.message || '';

        switch (status) {
          case 400: {
            // Bad Request - validation errors
            // Check if there are field-specific errors
            if (responseData?.errors && typeof responseData.errors === 'object') {
              const fieldErrors = {};
              Object.entries(responseData.errors).forEach(([apiField, message]) => {
                const formField = apiToFormFieldMap[apiField] || apiField;
                fieldErrors[formField] = message;
              });
              setApiFieldErrors(fieldErrors);
            }
            toast.error(apiMessage || 'Invalid data provided. Please check your inputs.');
            break;
          }

          case 401:
            // Unauthorized
            toast.error('Session expired. Please log in again.');
            break;

          case 403:
            // Forbidden
            toast.error('You do not have permission to perform this action.');
            break;

          case 404:
            // Not Found
            toast.error('Sales person not found. Please refresh and try again.');
            break;

          case 409: {
            // Conflict - duplicate data - set field-level errors
            if (responseData?.conflictingFields) {
              const fieldErrors = {};
              const conflictFields = Object.keys(responseData.conflictingFields);

              conflictFields.forEach((apiField) => {
                const formField = apiToFormFieldMap[apiField] || apiField;
                const displayName = fieldDisplayNames[apiField] || apiField;
                fieldErrors[formField] = `${displayName} already exists. Please use a different value.`;
              });

              // Set field errors to display on the form
              setApiFieldErrors(fieldErrors);

              const fieldNames = conflictFields.map((field) => fieldDisplayNames[field] || field);
              toast.error(`${fieldNames.join(', ')} already exists. Please use a different value.`);
            } else {
              toast.error(apiMessage || 'A record with this information already exists.');
            }
            break;
          }

          case 422: {
            // Unprocessable Entity - validation errors
            if (responseData?.errors && typeof responseData.errors === 'object') {
              const fieldErrors = {};
              Object.entries(responseData.errors).forEach(([apiField, message]) => {
                const formField = apiToFormFieldMap[apiField] || apiField;
                fieldErrors[formField] = message;
              });
              setApiFieldErrors(fieldErrors);
            }
            toast.error(apiMessage || 'Invalid data. Please check your inputs and try again.');
            break;
          }

          case 500:
          case 502:
          case 503:
            // Server errors
            toast.error('Server error. Please try again later.');
            break;

          default:
            toast.error(apiMessage || `An error occurred (${status}). Please try again.`);
        }
      } else if (error.request) {
        // Network error - no response received
        toast.error('Unable to connect to server. Please check your internet connection.');
      } else {
        // Other error
        toast.error(error.message || 'An unexpected error occurred. Please try again.');
      }
      // Do NOT reset/close the form on error
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Prepares the form for updating an existing sales team member
   * Maps API data to form fields and opens the edit modal
   * @param {string} sales_person_id - ID of the sales person to be updated
   */
  const prepareUpdate = (sales_person_id) => {
    const d = data.find((d) => d.sales_person_id === sales_person_id);
    if (!d) {
      toast.error('Sales person not found');
      return;
    }

    // Map API data to form fields
    const currentSalesTeam = {
      _id: d.sales_person_id, // Used for identifying if it's an update
      firstName: d.first_name || '',
      middleName: d.middle_name || '',
      lastName: d.last_name || '',
      preferredName: d.preferred_name || '',
      jobTitle: d.job_title || '',
      reportingManager: d.manager_id || '', // Used for form field
      loginId: d.user_id !== undefined && d.user_id !== null ? String(d.user_id) : '', // User ID for Login ID dropdown (string for select)
      mobileNum: formatPhoneNumber(d.mobile || ''), // Format phone for display
      email: d.email || '',
      contactName: d.contact_name || '',
      contactPhoneNum: formatPhoneNumber(d.contact_mobile || ''), // Format phone for display
      contactEmail: d.contact_email || '',
      contactRelation: d.relation || '',
      notes: d.notes || '',
      salesStatus: d.status || 'active',
      // Metadata
      created: d.created || '',
      updated: d.updated || '',
      created_by: d.created_by || '',
    };

    setSalesTeam(currentSalesTeam);
    reset(currentSalesTeam);
    setApiFieldErrors({}); // Clear any previous API errors
    setIsModalOpen(true);
    fetchModalData(); // Load managers and users when modal opens
    document.body.style.overflow = 'hidden';
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim().toLowerCase();

    return (
      fullName.includes(searchLower) ||
      (item.job_title || '').toLowerCase().includes(searchLower) ||
      (item.email || '').toLowerCase().includes(searchLower) ||
      (item.mobile || '').toLowerCase().includes(searchLower) ||
      (item.manager_name || '').toLowerCase().includes(searchLower) ||
      (item.status || '').toLowerCase().includes(searchLower)
    );
  });

  // Initial data fetch — only load the sales team list for the table
  useEffect(() => {
    getData(false);
  }, []);

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <SalesTeamHeader
          onReload={() => getData(true)}
          onAddSalesPerson={openModal}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <SalesTable
            data={filteredData}
            prepareUpdate={prepareUpdate}
            isFetching={isLoading}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit modal — conditionally rendered so form resets on each open */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={handleClose} size="5xl">
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-xl font-semibold">
              {salesTeam._id ? (
                <>
                  Edit Sales Person
                  {(salesTeam.firstName || salesTeam.middleName || salesTeam.lastName) && (
                    <span style={{ color: 'rgb(178, 120, 33)', marginLeft: 8 }}>
                      -{' '}
                      {[salesTeam.firstName, salesTeam.middleName, salesTeam.lastName]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  )}
                </>
              ) : (
                'Add Sales Person'
              )}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close"
              type="button"
            >
              &times;
            </button>
          </div>
          <CreateEditForm
            initialData={salesTeam}
            onSubmit={onSubmit}
            closeModal={handleClose}
            jobs={jobs}
            relations={relations}
            managers={managers}
            refSalesLoading={managersLoading}
            users={users}
            usersLoading={usersLoading}
            isSubmitting={isSubmitting}
            apiFieldErrors={apiFieldErrors}
            submitButtonLabel={salesTeam._id ? 'Update Details' : 'Add Sales user'}
          />
        </Modal>
      )}
    </div>
  );
};

export default SalesTeams;
